import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { AbstractExternalExam } from '../../../../../../api/types/settings/studies/abstract-external-exam';
import { Major } from '../../../../../../api/types/settings/studies/major';
import globalSettingsStores from '../../../../../../stores/settings-stores';
import CancelButton from '../../../../../../component/ui/buttons/cancel-button/cancel-button';
import ApproveButtons from '../../../../../../component/ui/buttons/approve-button/approve-buttons';

interface SelectExamDialogProps {
  visible: boolean;
  onHide: () => void;
  onSelectedExams: (exams: AbstractExternalExam[]) => void;
  alreadySelectedExamIds: number[]; // To filter out already selected exams
}

export const SelectExamDialog: React.FC<SelectExamDialogProps> = ({
  visible,
  onHide,
  onSelectedExams,
  alreadySelectedExamIds,
}) => {
  const [selectedMajor, setSelectedMajor] = useState<Major | null>(null);
  const [selectedExams, setSelectedExams] = useState<AbstractExternalExam[]>(
    [],
  );

  // Reset state when dialog is closed or opened
  useEffect(() => {
    if (!visible) {
      setSelectedMajor(null);
      setSelectedExams([]);
    }
  }, [visible]);

  // Filter exams by selected major and exclude already selected exams
  const availableExams = useMemo(() => {
    if (!selectedMajor) {
      return [];
    }
    return globalSettingsStores.externalExams.filter(
      (exam) =>
        exam.majorId === selectedMajor.id &&
        !alreadySelectedExamIds.includes(exam.id),
    );
  }, [selectedMajor, alreadySelectedExamIds]);

  const handleApprove = () => {
    if (selectedExams.length === 0) {
      return;
    }
    onSelectedExams(selectedExams);
    setSelectedExams([]);
    setSelectedMajor(null);
    onHide();
  };

  return (
    <Dialog
      header="בחירת מבחנים"
      visible={visible}
      onHide={onHide}
      style={{
        width: '600px',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '8px',
      }}
      modal
    >
      <div style={{ direction: 'rtl', padding: '8px' }}>
        {/* Step 1: Select Major */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '14px',
              color: '#4b5563',
              marginBottom: '8px',
              display: 'block',
            }}
          >
            בחירת מגמה
          </label>
          <Dropdown
            value={selectedMajor}
            onChange={(e) => {
              setSelectedMajor(e.value);
              setSelectedExams(
                globalSettingsStores.externalExams.filter(
                  (item) => item.majorId === e.value.id,
                ),
              ); // Reset selected exams when major changes
            }}
            options={globalSettingsStores.majors}
            optionLabel="majorName"
            placeholder="בחירת מגמה"
            filter
            filterBy="majorName"
            style={{
              width: '100%',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
            }}
          />
        </div>

        {/* Step 2: Select Exams (only shown when major is selected) */}
        {selectedMajor && (
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              בחירת מבחנים
            </label>
            <DataTable
              emptyMessage={
                <div style={{ textAlign: 'right' }}>
                  {availableExams.length === 0
                    ? 'לא נמצאו מבחנים למגמה זו'
                    : 'לא נמצאו מבחנים'}
                </div>
              }
              selectionMode="multiple"
              selection={selectedExams}
              onSelectionChange={(e) =>
                setSelectedExams(e.value as AbstractExternalExam[])
              }
              value={availableExams}
              dataKey="id"
              tableStyle={{
                direction: 'rtl',
                marginTop: '8px',
              }}
            >
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
              />
              <Column
                style={{ textAlign: 'right' }}
                field="examName"
                header="שם מבחן"
              />
              <Column
                style={{ textAlign: 'right' }}
                field="symbol"
                header="סמל"
              />
            </DataTable>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          <CancelButton onClick={onHide} />
          <ApproveButtons onClick={handleApprove} />
        </div>
      </div>
    </Dialog>
  );
};
